import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useLocalize } from 'localize-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { JourneyStatusType, IUnresolvedAlert, UnresolvedAlertType, IJourney } from '@transferz/fe-models';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import { Card } from '@transferz/fe-component-library/src/components/card/Card';
import { ContentHeader } from '@transferz/fe-component-library/src/components/contentHeader/ContentHeader';
import Calendar from '@transferz/fe-component-library/src/components/icons/CalendarIcon';
import Location from '@transferz/fe-component-library/src/components/icons/LocationIcon';
import Clock from '@transferz/fe-component-library/src/components/icons/ClockIcon';
import PlaneTakeOff from '@transferz/fe-component-library/src/components/icons/PlaneTakeOffIcon';
import PlaneLanding from '@transferz/fe-component-library/src/components/icons/PlaneLandingIcon';
import PlaneTop from '@transferz/fe-component-library/src/components/icons/PlaneTopIcon';
import { NotificationVariants } from '@transferz/fe-component-library/src/types/notification';
import { Loading } from '../../components/loading/Loading';
import { ExtendedCard } from '../../components/card/ExtendedCard';
import { formatDate, formatTime, getTimeZoneShortNameAndOffset } from '../../utils/utils';
import { useQueryJourneysByBookingCode } from '../../queries/booking/hooks/useQueryJourneysByBookingCode';
import { useQueryJourneyChangesByJourneyId } from '../../queries/booking/hooks/useQueryJourneyChangesByJourneyId';
import { useQueryUnresolvedAlerts } from '../../queries/booking/hooks/useQueryUnresolvedAlerts';
import { BookingStatus } from '../journey/components/bookingStatus/BookingStatus';
import { UnresolvedAlerts } from './components/UnresolvedAlerts';
import { useMutationResolveAlert } from '../../queries/booking/hooks/useMutationResolveAlert';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import './booking.scss';

const Booking: React.FC<any> = ({ params: { bookingCode } }) => {
  const flags = useFlags();
  const { translate } = useLocalize();
  const [, setLocation] = useLocation();
  const [, , removeValue] = useLocalStorage('initAlertResolutionNotification');
  const [unresolvedAlerts, setUnresolvedAlerts] = useState<any>([]);
  const [statuses, setStatuses] = useState<any>({});
  const [changesLoading, setChangesLoading] = useState(true);
  const [showAlertResolveNotification, setShowAlertResolveNotification] = useState(false);
  const [alertResolveNotificationMessage, setAlertResolveNotificationMessage] = useState('' as any);
  const [alertResolvenotificationVariant, setAlertResolvenotificationVariant] = useState('' as any);
  const urlParams: any = new URLSearchParams(window.location.search);
  const phoneNumberVerified = urlParams.get('phone-number-verified');
  const token = urlParams.get('token');
  const { data: journeysData, loading: journeysLoading } = useQueryJourneysByBookingCode({ variables: { bookingCode } });
  const { refetch: getJourneyChangeByJourneyId } = useQueryJourneyChangesByJourneyId({ variables: { id: 0, skip: true } });
  const { refetch: getUnresolvedAlerts } = useQueryUnresolvedAlerts({ variables: { params: {
    bookingId: 1,
  }, skip: true } });
  const journeysList = journeysData?.journeysByBookingCode?.results?.slice().sort((a: any, b: any) => a.journeyExecutionInfo?.pickupDate > b.journeyExecutionInfo?.pickupDate ? 1 : -1);
  const [resolveAlert] = useMutationResolveAlert();
  const handleGetUnresolvedAlerts = () => {
    setUnresolvedAlerts([]);
    getUnresolvedAlerts({
      params: {
        bookingId: journeysList[0]?.bookingId,
      },
      skip: false,
    })
      .then(({ data }: any) => {
        const unresolvedAlertsData = [];
        const incorrectPhoneAlert = data?.unresolvedAlerts.find((alert: IUnresolvedAlert) => alert.type === UnresolvedAlertType.IncorrectTravellerPhone);
        const flightNumberAlerts = data?.unresolvedAlerts
          .filter((alert: IUnresolvedAlert) => {
            return alert.type === UnresolvedAlertType.FlightNumberNotFound
              || alert.type === UnresolvedAlertType.FlightNumberIncorrectAirport
              || alert.type === UnresolvedAlertType.FlightNumberNotProvided;
          });
        if (incorrectPhoneAlert) {
          unresolvedAlertsData.push({ ...incorrectPhoneAlert, phone: journeysList[0]?.travellerInfo?.phone });
        }
        if (flightNumberAlerts.length && flags?.showFlightNumberAlerts) {
          flightNumberAlerts.map((flightNumberAlert: any) => {
            const journeyId = flightNumberAlert.journeyIds[0];
            const journey = journeysList.find((journey: IJourney) => journey.id === journeyId);
            const hubIataCode = journey?.hub?.details.filter((detail: any) => (detail.code === 'iata'))[0]?.value;
            unresolvedAlertsData.push({
              ...flightNumberAlert,
              journeyCode: journey.journeyCode,
              journeyId: journey.id,
              flightNumber: journey.travellerInfo.flightNumber,
              travellerInfo: journey.travellerInfo,
              pickupDate: journey?.journeyExecutionInfo?.pickupDate,
              isJourneyInbound: journey?.originLocation?.hub,
              hubIataCode,
            });
          });
        }
        setUnresolvedAlerts(unresolvedAlertsData);
      });
  };
  useEffect(() => {
    if (!journeysLoading) {
      let statuses: any = {};
      if (flags?.showUnresolvedAlerts && !phoneNumberVerified && !token) {
        handleGetUnresolvedAlerts();
      }
      if (phoneNumberVerified && token) {
        resolveAlert({
          variables: {
            token,
          },
        })
          .then(() => {
            setAlertResolvenotificationVariant(NotificationVariants.success);
            setAlertResolveNotificationMessage(translate('h_successfully_verified_phone'));
          })
          .catch(() => {
            setAlertResolvenotificationVariant(NotificationVariants.error);
            setAlertResolveNotificationMessage(translate('booking_somethingWentWrong'));
            handleGetUnresolvedAlerts();
          })
          .finally(() => {
            window.history.replaceState({}, document.title, window.location.pathname);
            removeValue('initAlertResolutionNotification');
            setShowAlertResolveNotification(true);
          });
      }
      for (const journey of journeysList) {
        getJourneyChangeByJourneyId({ id: journey.id, skip: false })
          .then(({ data }: any) => {
            const valueChanges = data?.journeyChangesByJourneyId?.map((change: any) => change.valueChanges);
            let driverUnderwayChanges: any = [];
            valueChanges?.map((valueChange: any) => {
              return valueChange?.find((change: any) => {
                if (change?.fieldName === 'assignedDriverId' && change?.newValue === null && journey.status === JourneyStatusType.Confirmed) {
                  driverUnderwayChanges.push(change);
                  return;
                }
              });
            });
            if (driverUnderwayChanges?.length >= 1) {
              statuses[journey.id] = { status: JourneyStatusType.DriverUnderway };
            } else {
              statuses[journey.id] = { status: journey.status };
            }
            if (Object.keys(statuses).length === journeysList.length) {
              setStatuses(statuses);
              setChangesLoading(false);
            }
          });
      }
    }
  }, [journeysData]);
  return (
    <div className="booking">
      {
        flags?.showNewDesign && (
          <ContentHeader
            title={translate('yourBooking')}
            bookingCode={bookingCode}
          />
        )}
      <div className="booking-content">
        {(journeysLoading || changesLoading) && !statuses.length
          ? <Loading />
          : flags?.showNewDesign
            ? (
              <>
                <UnresolvedAlerts
                  unresolvedAlerts={unresolvedAlerts}
                  showAlertResolveNotification={showAlertResolveNotification}
                  alertResolveNotificationMessage={alertResolveNotificationMessage}
                  alertResolvenotificationVariant={alertResolvenotificationVariant}
                  refetchUnresolvedAlerts={handleGetUnresolvedAlerts}
                />
                {!!journeysList?.length && journeysList?.map((journey: any, index: number) => {
                  return (
                    <div className="card-wrap" key={journey?.journeyCode}>
                      <Card
                        title={`${translate('Journey')} ${journey?.journeyCode}`}
                        titleColor="blue5"
                        chevronDirection="right"
                        collapsibleIcon={false}
                        collapsed={false}
                        onClick={() => setLocation(`/booking/${bookingCode}/journey/${journey.id}`)}
                      >
                        <BookingStatus
                          journey={journey}
                          statuses={statuses}
                        />
                        <div className="trip">
                          <div className="trip-address">
                            <div className="pb-32 trip-info-item info-first">
                              {journey?.inbound ? <Location className="trip-icon icon-location" /> : <PlaneLanding className="trip-icon trip-icon-plane" width="23" height="28" />}
                              <Typography variant="body1">
                                {journey?.originLocation?.hub ? journey?.hub?.autoCompleteName : journey?.originLocation?.address?.originalAddress}
                              </Typography>
                              {!journey?.originLocation?.hub && <Typography variant="body1" color="asphalt5">{journey?.originLocation?.address?.formattedAddress}</Typography>}
                            </div>
                            <div className="trip-info-item">
                              {journey?.inbound ? <PlaneTakeOff className="trip-icon trip-icon-plane" width="23" height="28" /> : <Location className="trip-icon icon-location" />}
                              <Typography variant="body1">
                                {journey?.destinationLocation?.hub ? journey?.hub?.autoCompleteName : journey?.destinationLocation?.address?.originalAddress}
                              </Typography>
                              {!journey?.destinationLocation?.hub && <Typography variant="body2" color="asphalt5">{journey?.destinationLocation?.address?.formattedAddress}</Typography>}
                            </div>
                          </div>
                          <div className="trip-date">
                            <div className="trip-date-info">
                              <Calendar className="icon-calendar" />
                              <Typography variant="body1">{formatDate(journey?.journeyExecutionInfo?.pickupDate, 'DD-MM-YYYY')}</Typography>
                            </div>
                            <div className="trip-date-info">
                              <Clock className="icon-calendar" direction="right" />
                              <Typography variant="body1">{formatTime(journey?.journeyExecutionInfo?.pickupDate, 'HH:mm')}</Typography>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  );
                })}
              </>
            )
            : (
              <div className="booking-content-success">
                <h1 className="title-primary">{translate('bookingDetails')}</h1>
                <div className="text-bold">{translate('bookingNumber')} {bookingCode}</div>
                <div className="text-bold trips-count">{translate('trips')}: {journeysList?.length}</div>
                <div className="booking-list">
                  {journeysLoading && <Loading />}
                  {!!journeysList?.length && journeysList?.map((journey: any, index: number) => {
                    const title = index < 1 ? `${translate('bookedTrips')}` : '';
                    return (
                      <ExtendedCard title={title} key={index} onClickHandler={() => setLocation(`/booking/${bookingCode}/journey/${journey.id}`)}>
                        <div className="label-gray">{translate('tripCode')}</div>
                        <div className="trip-code">{journey?.journeyCode}</div>
                        <div className="trip-info">
                          <div className="trip-info-item">
                            <Calendar className="trip-icon icon-calendar" />
                            <span className="date-time">
                              <span className="text-bold">
                                <span>{formatDate(journey?.journeyExecutionInfo?.pickupDate)}</span>
                                <span>{formatTime(journey?.journeyExecutionInfo?.pickupDate)} {getTimeZoneShortNameAndOffset(journey?.journeyExecutionInfo?.pickupTimeZone)}</span>
                              </span>
                            </span>
                            {journey?.travellerInfo?.flightNumber && !journey?.inbound && (
                              <span className="flight-number">
                                <PlaneTop className="trip-icon icon-plane" width="23" height="28" /> {journey?.travellerInfo?.flightNumber}
                              </span>
                            )}
                            {journey?.travellerInfo?.flightNumber && journey?.inbound && (
                              <span className="flight-number">
                                <PlaneTop className="trip-icon icon-plane" width="12" height="15" /> {journey?.travellerInfo?.flightNumber}
                              </span>
                            )}
                          </div>
                          <div className="trip-info-item info-first">
                            {journey?.inbound ? <Location className="trip-icon icon-location" /> : <PlaneLanding className="trip-icon trip-icon-plane" width="23" height="28" />}
                            <div className="text-bold">
                              {journey?.originLocation?.hub ? journey?.hub?.autoCompleteName : journey?.originLocation?.address?.originalAddress}
                            </div>
                            <div className="text-normal">{journey?.originLocation?.address?.formattedAddress}</div>
                          </div>
                          <div className="trip-info-item">
                            {journey?.inbound ? <PlaneTakeOff className="trip-icon trip-icon-plane" width="23" height="28" /> : <Location className="trip-icon icon-location" />}
                            <div className="text-bold">
                              {journey?.destinationLocation?.hub ? journey?.hub?.autoCompleteName : journey?.destinationLocation?.address?.originalAddress}
                            </div>
                            <div className="text-normal">{journey?.destinationLocation?.address?.formattedAddress}</div>
                          </div>
                        </div>
                        <div className="trip-info-footer">
                          <div
                            className="extended-card-edit-btn details-btn"
                            onClick={() => setLocation(`/booking/${bookingCode}/journey/${journey.id}`)}>
                            {translate('details')}
                          </div>
                        </div>
                      </ExtendedCard>
                    );
                  })}
                </div>
              </div>
            )
        }
      </div>
    </div>
  );
};

export default Booking;
